import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { fingerprint, resumableUpload } from "@/lib/resumableUpload";
import { compressAudio, shouldCompress } from "@/lib/compressAudio";
import { UploadProgress } from "@/components/app/UploadProgress";
import { requestDoneNotifications } from "@/components/app/recordingAlert";

const Ctx = createContext(null);
const FORM_PAGES = ["/app/upload", "/app/record"];

export const UploadProvider = ({ children }) => {
  const { L } = useApp();
  const navigate = useNavigate();
  const loc = useLocation();
  const [state, setState] = useState(null);
  const ctrl = useRef(null);
  const path = useRef(loc.pathname);
  path.current = loc.pathname;

  useEffect(() => {
    if (!state) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state]);

  const start = async ({ blob: input, filename: name, workspaceId, meta }) => {
    let blob = input, filename = name;
    if (state) return toast.error(L("Satu muat naik sedang berjalan. Tunggu sehingga selesai.", "An upload is already in progress. Please wait.")), false;
    ctrl.current = { paused: false, cancelled: false };
    setState({ status: "preparing", loaded: 0, total: blob.size, filename });
    requestDoneNotifications();
    try {
      let fp;
      if (shouldCompress(blob, filename)) {
        setState((x) => ({ ...x, status: "compressing", compressPct: 0 }));
        const original = await fingerprint(blob);
        const small = await compressAudio(blob, { ctrl: ctrl.current, onProgress: (p) => setState((x) => (x ? { ...x, compressPct: p } : x)) }).catch((e) => {
          if (ctrl.current.cancelled) throw new Error("cancelled");
          console.warn("compression skipped", e);
          return null;
        });
        if (ctrl.current.cancelled) throw new Error("cancelled");
        if (small) {
          fp = `${original}-c${(await fingerprint(small)).slice(0, 12)}`;
          setState((x) => ({ ...x, originalSize: blob.size, total: small.size }));
          blob = small;
          filename = `${filename.replace(/\.[^.]+$/, "")}.webm`;
        }
      }
      const data = await resumableUpload({ blob, filename, workspaceId, meta, ctrl: ctrl.current, fp, onState: (s) => setState((x) => (x ? { ...x, ...s } : x)) });
      setState(null);
      const url = `/app/meetings/${data.id}`;
      const onForm = FORM_PAGES.includes(path.current);
      if (onForm) navigate(url);
      toast.success(L("Rakaman diterima. Pemprosesan bermula.", "Recording received. Processing started."), onForm ? {} : { duration: 10000, action: { label: L("Lihat", "View"), onClick: () => navigate(url) } });
      return true;
    } catch (e) {
      setState(null);
      if (ctrl.current.cancelled) { toast(L("Muat naik dibatalkan.", "Upload cancelled.")); return false; }
      if (e.code && e.message?.startsWith("blocked:")) {
        toast.error(L(`Muat naik gagal berulang kali (kod ${e.code}). Rangkaian anda (cth. firewall pejabat) mungkin menyekat muat naik — cuba guna data telefon, kemudian pilih fail yang sama untuk sambung.`, `Upload failed repeatedly (code ${e.code}). Your network (e.g. office firewall) may be blocking uploads — try mobile data, then pick the same file to resume.`), { duration: 15000 });
        return false;
      }
      toast.error(e.response ? formatApiError(e) :  L("Muat naik terhenti. Pilih fail yang sama untuk sambung dari tempat ia berhenti.", "Upload stopped. Pick the same file to resume where it left off."));
      return false;
    }
  };

  const pause = () => { ctrl.current.paused = true; ctrl.current.abort?.abort(); setState((x) => ({ ...x, status: "paused" })); };
  const resume = () => { ctrl.current.paused = false; setState((x) => ({ ...x, status: "uploading" })); };
  const cancel = () => {
    ctrl.current.cancelled = true;
    ctrl.current.abort?.abort();
    ctrl.current.cancelCompress?.();
    if (ctrl.current.uploadId) api.delete(`/uploads/${ctrl.current.uploadId}`).catch(() => {});
  };

  return (
    <Ctx.Provider value={{ start, busy: !!state }}>
      {children}
      {state && <UploadProgress {...state} compact={!FORM_PAGES.includes(loc.pathname)} onPause={pause} onResume={resume} onCancel={cancel} />}
    </Ctx.Provider>
  );
};

export const useUpload = () => useContext(Ctx);
